package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import kotlin.math.roundToInt
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

private const val PROGRESS_CELL_COUNT = 20

data class LProgressCounts(
    val total: Int,
    val translated: Int,
    val approved: Int,
) {
    init {
        require(total >= 0) { "LProgressCounts total must be non-negative" }
        require(translated >= 0) { "LProgressCounts translated must be non-negative" }
        require(approved >= 0) { "LProgressCounts approved must be non-negative" }
        require(approved <= total && translated <= total - approved) {
            "LProgressCounts translated and approved are exclusive and cannot exceed total"
        }
    }

    val completed: Int
        get() = translated + approved

    val empty: Int
        get() = total - completed

    val completionPercent: Int
        get() = if (total == 0) 0 else (completed * 100.0 / total).roundToInt().coerceIn(0, 100)
}

val LProgressBarStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
            property("gap", Space.SM.toString())
        }
}

val LProgressCellsStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .styleModifier {
            property("display", "grid")
            property("gap", Space.XXS.toString())
            property("grid-template-columns", "repeat($PROGRESS_CELL_COUNT, minmax(0, 1fr))")
        }
}

val LProgressCellStyle = CssStyle.base {
    Modifier
        .height(Space.LG)
        .borderRadius(Radius.Control)
        // A cell turning green is the single most repeated piece of feedback in the product — it
        // happens on every save. Snapping makes a save feel like a repaint; easing the colour makes
        // it feel like the thing was accepted. Colour only: the cell must not move, because a row
        // of them shifting would read as the layout reflowing.
        .transitionColors(durationMs = Motion.StandardMs)
}

/**
 * The remaining work, which has to be visible or the denominator is a lie.
 *
 * **`borderStrong`, not `border`.** An empty cell is a transparent box and its 1px edge is the
 * whole graphic — there is nothing else to see — so it is a 3:1 object, not a hairline divider.
 * `border` (#DCD5C6, 1.32:1) is documented as "dividers only — illegal as a control boundary": in
 * dark the last cells vanished into the card ground and a 36-of-45 bar read as roughly 80% done.
 * `EditorBudgetMeter` already states this exact rule for its own empty track, and
 * [LProgressCompactTrack] repeats it for the compact bar, which is where the argument used to be
 * sharpest — a compact cell was four pixels wide before it became a continuous track.
 */
val LProgressEmptyCellStyle = CssStyle.base {
    Modifier
        .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Colors.Transparent)
        .border(1.px, LineStyle.Solid, ColorTokens.BorderStrong.value())
}

// Follows the state rail: translated is grey, not blue. A 16% wash of `info` here put the same
// slate-blue next to the accent that the queue gutter had to give up for the same reason.
//
// The hue now comes from `ChartPalette.forState` rather than being spelled out again here. It used
// to be spelled out, and `LProgressRing` — plotting the identical `LProgressCounts` — spelled it
// out as `info`, the exact blue the paragraph above rejects. Two components disagreeing about what
// "translated" looks like is not a thing a comment can prevent; a shared function is. The rendered
// colour is unchanged: this is still `textMuted` washed at 28% under its own edge.
val LProgressTranslatedCellStyle = CssStyle.base {
    Modifier
        .backgroundColor(
            ChartPalette.forState(colorMode, ChartState.TRANSLATED)
                .tintOn(colorMode.toSitePalette().surface, ChartPalette.STATE_FILL_TINT),
        )
        .border(1.px, LineStyle.Solid, ChartPalette.forState(colorMode, ChartState.TRANSLATED))
}

val LProgressApprovedCellStyle = CssStyle.base {
    Modifier
        .backgroundColor(ChartPalette.forState(colorMode, ChartState.APPROVED))
        .border(1.px, LineStyle.Solid, ChartPalette.forState(colorMode, ChartState.APPROVED))
}

val LProgressCountsStyle = CssStyle.base {
    Modifier
        .color(ColorTokens.TextSecondary.value())
        .fontSize(TypeScale.Small)
        .tabularNumbers()
        .styleModifier {
            property("display", "flex")
            property("flex-wrap", "wrap")
            property("gap", "${Space.XS} ${Space.LG}")
            property("justify-content", "space-between")
            property("line-height", TypeScale.BodyLineHeight.toString())
        }
}

@Composable
fun LProgressBar(
    progress: LProgressCounts,
    modifier: Modifier = Modifier,
    label: String = t("ds.ds.lprogress_bar.translation_progress", "Translation progress"),
    showCounts: Boolean = true,
    /**
     * The bar reduced to its cells, for a header rather than a card.
     *
     * A page header states identity and task; the state belongs in the body. But a queue header
     * does have to say how far along the work is, and the full bar — sixteen-pixel cells plus two
     * lines of counts — is a body-sized thing that pushes the header's own words down the page. So
     * compact drops both captions and the numbers are said once, beside the bar, by whatever put it
     * there. It also stops being twenty cells: see [LProgressCompactTrack] for why half-height
     * cells are dots rather than a bar, and what replaced them.
     *
     * Nothing is dropped from the *accessible* representation: `aria-valuetext` still carries the
     * whole sentence, because the caption a sighted reader loses here was never what a screen
     * reader was getting — see [accessibleValueText].
     */
    compact: Boolean = false,
) {
    Div(
        LProgressBarStyle.toModifier()
            .then(modifier)
            .toAttrs {
                attr("role", "progressbar")
                attr("aria-label", label)
                attr("aria-valuemin", "0")
                attr("aria-valuemax", "100")
                attr("aria-valuenow", progress.completionPercent.toString())
                attr("aria-valuetext", progress.accessibleValueText())
            },
    ) {
        if (compact) {
            LProgressCompactTrack(progress)
        } else {
            Div(LProgressCellsStyle.toModifier().toAttrs {
                attr("aria-hidden", "true")
            }) {
                progress.toProgressCells().forEach { state ->
                    val stateModifier = when (state) {
                        LProgressCellState.EMPTY -> LProgressEmptyCellStyle.toModifier()
                        LProgressCellState.TRANSLATED -> LProgressTranslatedCellStyle.toModifier()
                        LProgressCellState.APPROVED -> LProgressApprovedCellStyle.toModifier()
                    }
                    Div(
                        LProgressCellStyle.toModifier()
                            .then(stateModifier)
                            .toAttrs(),
                    )
                }
            }
        }
        if (showCounts && !compact) {
            Div(LProgressCountsStyle.toModifier().toAttrs()) {
                Span {
                    Text(
                        tf(
                            "ds.ds.lprogress_bar.completed_of_total",
                            "{completed} of {total} complete",
                            "completed" to progress.completed,
                            "total" to progress.total,
                        ),
                    )
                }
                Span {
                    Text(
                        tf(
                            "ds.ds.lprogress_bar.bucket_breakdown",
                            "{approved} approved · {translated} translated · {empty} empty",
                            "approved" to progress.approved,
                            "translated" to progress.translated,
                            "empty" to progress.empty,
                        ),
                    )
                }
            }
        }
    }
}

/** The compact bar's height, from the same ladder the full bar's cells are cut from. */
private val COMPACT_TRACK_HEIGHT = Space.SM

/**
 * The compact bar, which is a track with a fill in it and never a row of dots.
 *
 * It used to be the twenty cells at half height. Twenty 4px-tall boxes, each with its own 8px
 * corner radius and its own 1px edge, separated by a 4px gap, are not a bar at any width the
 * product actually gives them: the review header's "0 of 12 decided" drew twelve hollow circles in
 * a line, and an empty one drew twenty — a dotted rule where the reader was promised a measure of
 * how far along the work is. The cells earn their separation in the full bar, where each is a
 * countable unit of work sixteen pixels tall; at half that height the separation is all there is.
 *
 * So compact is one continuous rounded track in `surfaceSunken` under a `borderStrong` outline —
 * the same "an empty track is a 3:1 object, not a hairline" argument [LProgressEmptyCellStyle]
 * makes, which is why the outline is visible on its own at 0 of N — with the completed share drawn
 * over it as solid fills in the state colours. Solid, not the full bar's 28% tint: a tint reads as
 * a fill because it sits inside its own border, and a segment of a continuous track has no border
 * of its own to be inside.
 *
 * **Nothing here transitions.** A cell turning green is a colour change and is eased; a fill
 * growing is a layout change, and the one thing this design system will not animate is layout.
 */
@Composable
private fun LProgressCompactTrack(progress: LProgressCounts) {
    val colorMode = ColorMode.current
    Div(
        Modifier
            .width(100.percent)
            .height(COMPACT_TRACK_HEIGHT)
            .backgroundColor(colorMode.toSitePalette().surfaceSunken)
            .border(1.px, LineStyle.Solid, ColorTokens.BorderStrong.value())
            .borderRadius(Radius.Pill)
            .styleModifier {
                property("display", "flex")
                // Clips both fills to the pill, so the leading edge of the bar is the leading edge
                // of the track rather than a square corner poking out of a rounded one.
                property("overflow", "hidden")
            }
            .toAttrs {
                attr("aria-hidden", "true")
                attr("data-progress-track", "compact")
            },
    ) {
        LProgressCompactFill(
            share = progress.sharePercent(progress.approved),
            color = ChartPalette.forState(colorMode, ChartState.APPROVED),
            state = "approved",
        )
        LProgressCompactFill(
            share = progress.sharePercent(progress.translated),
            color = ChartPalette.forState(colorMode, ChartState.TRANSLATED),
            state = "translated",
        )
    }
}

@Composable
private fun LProgressCompactFill(share: Double, color: Color, state: String) {
    // Absent rather than zero-width. An empty bucket has nothing to draw, and leaving a 0%-wide
    // node in the track would mean every reading of this bar — a test's, a future maintainer's —
    // has to know that a fill element does not imply a fill.
    if (share <= 0.0) return
    Div(
        Modifier
            .backgroundColor(color)
            .styleModifier { property("flex", "0 0 $share%") }
            .toAttrs { attr("data-progress-fill", state) },
    )
}

/** One bucket's share of the whole, to two decimals so a twelfth is not rounded into a pixel. */
private fun LProgressCounts.sharePercent(value: Int): Double =
    if (total == 0) 0.0 else (value * 10_000.0 / total).roundToInt() / 100.0

/**
 * What a screen reader hears in place of the segmented bar.
 *
 * Leads with counts, matching the visible label: `ProgressCell` renders "40 left" with the bar's
 * own counts hidden behind `aria-hidden`, so this string is not describing a bar to somebody who
 * can see it — it is substituting for one, and a percentage is not what the sighted reader gets.
 * `aria-valuenow` still carries the percentage; the `progressbar` role requires a number there, and
 * `aria-valuetext` only overrides what gets announced.
 */
internal fun LProgressCounts.accessibleValueText(): String = when {
    total == 0 -> tr("ds.ds.lprogress_bar.no_keys", "No keys")
    empty == 0 -> trf(
        "ds.ds.lprogress_bar.done_all_total_approved",
        "Done; all {total} approved",
        "total" to total,
    )
    else -> trf(
        "ds.ds.lprogress_bar.empty_left_of_total_approved_approved",
        "{empty} left of {total}; {approved} approved, {translated} translated",
        "empty" to empty,
        "total" to total,
        "approved" to approved,
        "translated" to translated,
    )
}

/**
 * What sits beside a progress bar, and inside a progress ring.
 *
 * A count, never a percent: `PRODUCT.md` principle 4. "Done" rather than "0 left" because zero
 * remaining is the good outcome and should not be drawn in the same shape as a workload.
 *
 * Moved here from `:site`'s `components/Common.kt` once [LProgressRing] needed it too — a second
 * component reaching for the identical label a page-level helper already computed was the sign
 * this belonged in the design system rather than in product code.
 */
fun progressTrailingLabel(counts: LProgressCounts): String = when {
    counts.total == 0 -> tr("ds.ds.lprogress_bar.no_keys", "No keys")
    counts.empty == 0 -> tr("ds.ds.lprogress_bar.done", "Done")
    else -> trf("ds.ds.lprogress_bar.empty_left", "{empty} left", "empty" to counts.empty)
}

private enum class LProgressCellState {
    EMPTY,
    TRANSLATED,
    APPROVED,
}

private fun LProgressCounts.toProgressCells(): List<LProgressCellState> {
    if (total == 0) return List(PROGRESS_CELL_COUNT) { LProgressCellState.EMPTY }

    val approvedBoundary = (approved * PROGRESS_CELL_COUNT.toDouble() / total)
        .roundToInt()
        .coerceIn(0, PROGRESS_CELL_COUNT)
    val completedBoundary = (completed * PROGRESS_CELL_COUNT.toDouble() / total)
        .roundToInt()
        .coerceIn(approvedBoundary, PROGRESS_CELL_COUNT)

    return List(PROGRESS_CELL_COUNT) { index ->
        when {
            index < approvedBoundary -> LProgressCellState.APPROVED
            index < completedBoundary -> LProgressCellState.TRANSLATED
            else -> LProgressCellState.EMPTY
        }
    }
}
